import { gql } from '@apollo/client';
import { client } from 'graphql/client';

const GET_AVAILABLE_HUBS = gql`
  mutation GetAvailableHubsFromZoneMappingFile($zoneMappingFileObjectKey: String!) {
    getAvailableHubsFromZoneMappingFile(zoneMappingFileObjectKey: $zoneMappingFileObjectKey)
  }
`;

const IMPORT_ZONE_MAPPINGS = gql`
  mutation ImportZoneMappings(
    $pickupGateFileObjectKey: String!
    $zoneMappingFileObjectKey: String!
    $selectedHubs: [String!]!
    $gatewayMapping: [StringMapEntryInput!]
    $reason: String
  ) {
    importZoneMappings(
      pickupGateFileObjectKey: $pickupGateFileObjectKey
      zoneMappingFileObjectKey: $zoneMappingFileObjectKey
      selectedHubs: $selectedHubs
      gatewayMapping: $gatewayMapping
      reason: $reason
    ) {
      success
      message
      totalProcessed
      successCount
      failedCount
      failedRecords {
        originZipcodePrefix
        targetZipcode
        gate
        errorMessage
        messageKey
      }
      changeSummary {
        insertedCount
        updatedCount
        deletedCount
        skippedCount
        records {
          action
          originZipcodePrefix
          targetZipcodeStart
          targetZipcodeEnd
          zone
          note
        }
      }
      changeLogObjectKey
      changeLogDownloadUrl
    }
  }
`;

export interface ZoneMappingImportResponse {
  success: boolean;
  message: string;
  totalProcessed: number;
  successCount: number;
  failedCount: number;
  failedRecords: ZoneMappingImportFailedRecord[];
  changeSummary?: ZoneMappingChangeSummary | null;
  changeLogObjectKey?: string | null;
  changeLogDownloadUrl?: string | null;
}

export interface ZoneMappingImportFailedRecord {
  originZipcodePrefix?: string;
  targetZipcode?: string;
  gate?: string;
  errorMessage: string;
  messageKey?: string;
}

export interface ZoneMappingChangeSummary {
  insertedCount: number;
  updatedCount: number;
  deletedCount: number;
  skippedCount: number;
  records: ZoneMappingChangeRecord[];
}

export type ZoneMappingChangeAction = 'INSERTED' | 'UPDATED' | 'DELETED';

export interface ZoneMappingChangeRecord {
  action: ZoneMappingChangeAction;
  originZipcodePrefix: string;
  targetZipcodeStart: string;
  targetZipcodeEnd: string;
  zone: number;
  note?: string | null;
}

export interface StringMapEntryInput {
  key: string;
  value: string;
}

/**
 * 获取Zone映射Excel文件中的可用HUB列
 */
export const getAvailableHubsFromZoneMappingFile = async (
  zoneMappingFileObjectKey: string
): Promise<string[]> => {
  try {
    const result = await client.mutate({
      mutation: GET_AVAILABLE_HUBS,
      variables: {
        zoneMappingFileObjectKey
      }
    });
    return result.data.getAvailableHubsFromZoneMappingFile || [];
  } catch (error) {
    console.error('Failed to get available hubs:', error);
    throw error;
  }
};

/**
 * 导入Zone映射数据
 */
export const importZoneMappings = async (
  pickupGateFileObjectKey: string,
  zoneMappingFileObjectKey: string,
  selectedHubs: string[],
  gatewayMapping?: Record<string, string>,
  reason?: string
): Promise<ZoneMappingImportResponse> => {
  try {
    // 转换gatewayMapping为GraphQL格式
    const gatewayMappingEntries: StringMapEntryInput[] | undefined = gatewayMapping
      ? Object.entries(gatewayMapping).map(([key, value]) => ({ key, value }))
      : undefined;

    const result = await client.mutate({
      mutation: IMPORT_ZONE_MAPPINGS,
      variables: {
        pickupGateFileObjectKey,
        zoneMappingFileObjectKey,
        selectedHubs,
        gatewayMapping: gatewayMappingEntries,
        reason
      }
    });
    return result.data.importZoneMappings;
  } catch (error) {
    console.error('Failed to import zone mappings:', error);
    throw error;
  }
};

